import { useMemo, useState } from "react";
import {
  generatePath,
  useParams,
  matchPath,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { useRecoilValue } from "recoil";
import LinkButton from "components/Buttons/LinkButton/LinkButton";
import { IOrderDetails } from "interfaces/Order";
import { URLRoutes } from "enums/Routing";
import Button from "components/Buttons/Button/Button";
import { hasOrderExpired, isOrderScheduled } from "helpers/OrderHelper";
import CreateEditOrderModal from "components/Modals/CreateEditOrderModal/CreateEditOrderModal";
import useSnackbar from "hooks/useSnackbar";
import Loading from "components/Loading/Loading";
import { IsSuperAdmin } from "recoil-states/auth-states";
import CancelOrderModal from "components/Modals/CancelOrderModal/CancelOrderModal";
import ReviewVoidRequestModal from "components/Modals/ReviewVoidRequestModal/ReviewVoidRequestModal";
import { OrderCancelType, OrderStatus } from "enums/Order";
import { formatDate } from "helpers/CommonHelper";
import OrderVersionCards from "components/OrderVersionCards/OrderVersionCards";
import { INavItem } from "interfaces/Common";
import Navbar from "components/Navbar/Navbar";
import OrderHistory from "components/OrderHistory/OrderHistory";
import OrderBillingHistory from "components/OrderBillingHistory/OrderBillingHistory";

interface OrderComponentProps {
  order: IOrderDetails | undefined;
  loading: boolean;
  onSuccessfullyEdited: () => void;
}

enum Modal {
  Edit = 1,
  Cancel = 2,
  Renew = 3,
  ReviewVoid = 4,
}

enum OrderTabs {
  Details = 1,
  History = 2,
  BillingHistory = 3,
}

const OrderComponent = (props: OrderComponentProps) => {
  const { order, loading, onSuccessfullyEdited } = props;

  const { clinicianId, orderId } = useParams();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const { snackbarComponent, setSnackbarError } = useSnackbar();

  const [activeModal, setActiveModal] = useState<Modal | undefined>(undefined);
  const [activeTab, setAtiveTab] = useState<OrderTabs>(OrderTabs.Details);

  const isSuperAdmin = useRecoilValue(IsSuperAdmin);
  // a rejected void request cancels the order; prior charges stand
  const voidRejected = Boolean(order?.meta?.void?.rejectedOn);

  const navItems: INavItem[] = [
    {
      label: "Order",
      onClick: () => setAtiveTab(OrderTabs.Details),
      isActive: activeTab === OrderTabs.Details,
    },
    {
      label: "Order History",
      onClick: () => setAtiveTab(OrderTabs.History),
      isActive: activeTab === OrderTabs.History,
    },
    // billing history is available only to super admin role
    ...(isSuperAdmin
      ? [
          {
            label: "Billing History",
            onClick: () => setAtiveTab(OrderTabs.BillingHistory),
            isActive: activeTab === OrderTabs.BillingHistory,
          },
        ]
      : []),
  ];

  const showViewResultsBtn = !matchPath(
    URLRoutes.PatientOrderDetails,
    pathname
  );

  // Clinical staff account holders, Clinician accounts and BioMech admins have the ability to edit or cancel an unexpired Order.
  const canEditOrCancelOrder = useMemo(
    (): boolean =>
      order
        ? order.status === OrderStatus.Opened &&
          order.cancelType !== OrderCancelType.VoidRequested && // locked while BioMech reviews the void request
          !hasOrderExpired(order) &&
          // (isSuperAdmin || isClientAdmin || auth?.type === UserType.Clinician)
          true // Allow all users to edit/cancel orders
        : false,
    [order]
  );

  return (
    <>
      <div className="h-full relative flex flex-col gap-4">
        {order?.patientId && activeTab === OrderTabs.Details && (
          <div className="flex gap-5 self-center relative md:fixed md:top-16 md:right-5 md:portrait:top-3.5 lg:top-3.5">
            {canEditOrCancelOrder && (
              <>
                <Button
                  className="border-2 border-pelorous !bg-white !text-pelorous"
                  onClick={() => setActiveModal(Modal.Edit)}
                >
                  Edit Order
                </Button>
                <Button
                  className="!bg-crimson"
                  onClick={() => setActiveModal(Modal.Cancel)}
                >
                  Cancel Order
                </Button>
              </>
            )}

            {order.canBeRenewed && !isOrderScheduled({ startDate: order.startDate }) && (
              <Button
                className="border-2 border-pelorous !bg-white !text-pelorous"
                onClick={() => setActiveModal(Modal.Renew)}
              >
                Renew Order
              </Button>
            )}

            {showViewResultsBtn && (
              <LinkButton
                to={
                  clinicianId
                    ? generatePath(URLRoutes.ClinicianPatientResults, {
                        clinicianId,
                        patientId: order.patientId,
                      })
                    : generatePath(URLRoutes.PatientResults, {
                        patientId: order.patientId,
                      })
                }
                className="bg-tropicalRainForest border-none !text-white"
              >
                View Results
              </LinkButton>
            )}
          </div>
        )}

        <div className="grow h-0 flex flex-col gap-4">
          {order && (
            <>
              <Navbar drawerTitle={`Order ${order.id}`} items={navItems} />

              {order.cancelType === OrderCancelType.VoidRequested && (
                <div className="mr-5 xl:mr-0 flex flex-wrap items-start justify-between gap-4 rounded-md border border-pattensBlue2 border-l-4 border-l-[#D69E2E] bg-white px-5 py-4">
                  <div className="text-sm">
                    <h3 className="text-base font-bold text-[#975A16] mb-1">
                      {isSuperAdmin
                        ? "Void request pending review"
                        : "Void request sent to BioMech"}
                    </h3>
                    <p className="text-fiord/70">
                      Submitted
                      {order.meta?.void?.requestedOn &&
                        ` ${formatDate(order.meta.void.requestedOn)}`}
                      {order.meta?.void?.requestedBy &&
                        ` by ${order.meta.void.requestedBy}`}
                    </p>
                    {order.meta?.void?.requestReason && (
                      <p className="mt-2">
                        <b>Reason:</b> {order.meta.void.requestReason}
                      </p>
                    )}
                    {!isSuperAdmin && (
                      <p className="mt-2">
                        The order has been sent to BioMech for review. You will
                        receive an email once the review is complete.
                      </p>
                    )}
                  </div>
                  {isSuperAdmin ? (
                    <Button
                      className="!bg-pelorous"
                      onClick={() => setActiveModal(Modal.ReviewVoid)}
                    >
                      Review Request
                    </Button>
                  ) : (
                    <span className="rounded-full bg-[#FDF3DC] px-3 py-1 text-xs font-bold text-[#975A16] whitespace-nowrap">
                      Pending review
                    </span>
                  )}
                </div>
              )}

              {order.cancelType === OrderCancelType.Voided && (
                <div className="mr-5 xl:mr-0 rounded-md border border-pattensBlue2 border-l-4 border-l-crimson bg-white px-5 py-4 text-sm">
                  <h3 className="text-base font-bold text-crimson mb-1">
                    Order voided
                  </h3>
                  <p className="text-fiord/70">
                    Voided
                    {order.meta?.void?.voidedOn &&
                      ` ${formatDate(order.meta.void.voidedOn)}`}
                    {order.meta?.void?.voidedBy && ` by ${order.meta.void.voidedBy}`}
                  </p>
                  {order.meta?.void?.voidReason && (
                    <p className="mt-2">
                      <b>Reason:</b> {order.meta.void.voidReason}
                    </p>
                  )}
                  <p className="mt-2">No further billing will occur for this order.</p>
                </div>
              )}

              {order.status === OrderStatus.Cancelled &&
                order.cancelType !== OrderCancelType.Voided && (
                  <div className="mr-5 xl:mr-0 rounded-md border border-pattensBlue2 border-l-4 border-l-[#9CA3AF] bg-white px-5 py-4 text-sm">
                    <h3 className="text-base font-bold text-[#6B7280] mb-1">
                      {voidRejected
                        ? `Order #${order.orderNum} is Canceled — Prior Charges Apply`
                        : "Order cancelled"}
                    </h3>
                    {order.meta?.cancelledOn && (
                      <p className="text-fiord/70">
                        Cancelled {formatDate(order.meta.cancelledOn)}
                      </p>
                    )}
                    {voidRejected ? (
                      <>
                        {order.meta?.void?.requestReason && (
                          <p className="mt-2">
                            <b>Void request reason:</b>{" "}
                            {order.meta.void.requestReason}
                          </p>
                        )}
                        {order.meta?.void?.rejectReason && (
                          <p className="mt-2">
                            <b>Reviewer note:</b> {order.meta.void.rejectReason}
                          </p>
                        )}
                      </>
                    ) : (
                      order.meta?.cancellationComment && (
                        <p className="mt-2">
                          <b>Reason:</b> {order.meta.cancellationComment}
                        </p>
                      )
                    )}
                    <p className="mt-2">
                      The current billing cycle is the final one for this order.
                    </p>
                  </div>
                )}

              {order.status === OrderStatus.Opened && hasOrderExpired(order) && (
                <div className="mr-5 xl:mr-0 rounded-md border border-pattensBlue2 border-l-4 border-l-endeavour bg-white px-5 py-4 text-sm">
                  <h3 className="text-base font-bold text-endeavour mb-1">
                    Order expired
                  </h3>
                  <p className="text-fiord/70">Ended {formatDate(order.endDate)}</p>
                </div>
              )}

              <div className="grow h-0 pr-5 xl:pr-0">
                {activeTab === OrderTabs.Details && (
                  <OrderVersionCards order={order} />
                )}

                {activeTab === OrderTabs.History && orderId && (
                  <OrderHistory orderId={orderId} onError={setSnackbarError} />
                )}

                {activeTab === OrderTabs.BillingHistory && order.patientId && (
                  <OrderBillingHistory
                    orderId={order.id}
                    patientId={order.patientId}
                  />
                )}
              </div>
            </>
          )}
        </div>

        {(activeModal === Modal.Edit || activeModal === Modal.Renew) && (
          <CreateEditOrderModal
            isRenew={activeModal === Modal.Renew}
            initialOrder={order}
            onClose={() => setActiveModal(undefined)}
            onInitialDataFetchFail={setSnackbarError}
            onCompleted={(orderId) => {
              if (activeModal === Modal.Renew) {
                navigate(`${URLRoutes.OrdersDashboard}/${orderId}`);
              } else {
                onSuccessfullyEdited();
              }
            }}
          />
        )}

        {order && activeModal === Modal.ReviewVoid && (
          <ReviewVoidRequestModal
            order={order}
            onClose={() => setActiveModal(undefined)}
            onSuccess={onSuccessfullyEdited}
          />
        )}

        {order && activeModal === Modal.Cancel && (
          <CancelOrderModal
            orderId={order.id}
            onClose={() => setActiveModal(undefined)}
            onSuccess={onSuccessfullyEdited}
          />
        )}

        <Loading loading={loading} />
      </div>

      {snackbarComponent}
    </>
  );
};

export default OrderComponent;
